import { useCallback, useEffect, useRef, useState, type MutableRefObject, type RefObject, type FC } from 'react';

// Lexical Import
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $setBlocksType } from '@lexical/selection';
import classNames from 'classnames/bind';
import { $createHeadingNode, $isHeadingNode, type HeadingTagType } from '@lexical/rich-text';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  type LexicalEditor,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode } from '@lexical/list';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { $isLinkNode } from '@lexical/link';

// Supportive imports
import DropDown, { DropDownType } from '../DropDown/DropDown';
import { type Data } from '../DropDown/types/propsType';
import { useClickOutsideDetector } from '../hooks';
import type { BtnStyleFormatType, ToolbarDropdownType, ToolbarPopupRefType, ToolbarProps } from './types/propsType';
import { blockTypeToBlockName, getSelectedNode, toolbarBtnList, listTypeToListName } from './subComponent/helper';
import { TablePopup, LinkPopup } from './subComponent/PopoverComponent';
import styles from './RichTextEditor.module.scss';
import { AdditionBtnStyle } from './types/enums';

const cx = classNames.bind(styles);

function Divider() {
  return <div className={styles['divider']} />;
}
const { HTMLCODE, Link, Table, Undo, Redo, left, center, right, Image, Listul, Listol } = AdditionBtnStyle;
function BlockFormatDropDown({
  editor,
  blockType,
}: {
  blockType: keyof typeof blockTypeToBlockName;
  editor: LexicalEditor;
}): JSX.Element {
  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      });
    }
  };

  const btnListDataSource = [
    {
      style: 'Normal',
      action: () => formatParagraph(),
    },
    {
      style: 'Heading 1',
      action: () => formatHeading('h1'),
    },
    {
      style: 'Heading 2',
      action: () => formatHeading('h2'),
    },
    {
      style: 'Heading 3',
      action: () => formatHeading('h3'),
    },
    {
      style: 'Heading 4',
      action: () => formatHeading('h4'),
    },
    {
      style: 'Heading 5',
      action: () => formatHeading('h5'),
    },
    {
      style: 'Heading 6',
      action: () => formatHeading('h6'),
    },
  ];
  return (
    <DropDown
      name="rte-block-list"
      dropDownType={DropDownType.INLINE}
      dropdownLabel={blockTypeToBlockName[blockType]}
      data={btnListDataSource}
      value={blockTypeToBlockName[blockType]}
      testId="rte-block-list"
      configData={{
        label: 'style',
        onSelect: (selected: Data) => {
          const { action } = selected as unknown as ToolbarDropdownType;
          action?.();
        },
      }}
      disabledFilter
    />
  );
}

const Toolbar: FC<ToolbarProps> = ({ disabledFeatureList, onImageUpload, uploadImageURL, isLoading, setIsLoading }) => {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const toolbarImageBtnRef = useRef() as MutableRefObject<HTMLButtonElement>;
  const toolbarTableBtnRef = useRef() as MutableRefObject<HTMLButtonElement>;
  const toolbarLinkBtnRef = useRef() as MutableRefObject<HTMLButtonElement>;
  // const toolbarEmojiBtnRef = useRef() as MutableRefObject<HTMLButtonElement>;

  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>('paragraph');
  const [currentPopup, setCurrentPopup] = useState<ToolbarPopupRefType>({
    ref: null,
    component: null,
  });
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isHighlight, setIsHighlight] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [listType, setListType] = useState<keyof typeof listTypeToListName>();

  // close upload pop when clicked outside
  useClickOutsideDetector(currentPopup?.ref as RefObject<HTMLElement>, () => {
    closePopup();
  });

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));
      setIsCode(selection.hasFormat('code'));
      setIsHighlight(selection.hasFormat('highlight'));
      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setListType(type as keyof typeof listTypeToListName);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (type in blockTypeToBlockName) {
            setListType(undefined);
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
        }
      }
    }
  }, [activeEditor]);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor, $updateToolbar],
  );

  useEffect(
    () =>
      mergeRegister(
        activeEditor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            $updateToolbar();
          });
        }),
      ),
    [$updateToolbar, activeEditor, editor],
  );
  /**
   * @description We need wait until user pass processed Image URL to be display.
   */
  useEffect(() => {
    if (isLoading && uploadImageURL) {
      setIsLoading(false);
    }
  }, [isLoading, uploadImageURL]);

  /**
   * @function generateHtmlFromText produces HTML from editor state
   */
  const generateHTMLFromText = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection) {
        editor.update(() => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(selection?.getTextContent() || '', 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          const paragraphNode = $createParagraphNode();
          nodes.forEach((node) => {
            paragraphNode.append(node);
          });
          selection.insertNodes([paragraphNode]);
        });
      }
    });
  };

  const fallBack = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };
  // UL
  const formatBulletList = () => {
    if (listType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      fallBack();
    }
  };
  // OL
  const formatNumberedList = () => {
    if (listType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      fallBack();
    }
  };
  // toolbar button click event handler
  const handleBtnClick = (style: BtnStyleFormatType) => {
    switch (style) {
      case HTMLCODE:
        generateHTMLFromText();
        break;
      case Link:
        setCurrentPopup({
          ref: toolbarLinkBtnRef,
          component: AdditionBtnStyle.Link,
        });
        break;
      case Undo:
        editor.dispatchCommand(UNDO_COMMAND, undefined);
        break;
      case Redo:
        editor.dispatchCommand(REDO_COMMAND, undefined);
        break;
      case left:
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
        break;
      case center:
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
        break;
      case right:
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
        break;
      case Listol:
        formatNumberedList();
        break;
      case Listul:
        formatBulletList();
        break;
      case Image:
        setCurrentPopup({
          ref: toolbarImageBtnRef,
          component: AdditionBtnStyle.Image,
        });
        break;
      case Table:
        setCurrentPopup({
          ref: toolbarTableBtnRef,
          component: AdditionBtnStyle.Table,
        });
        break;
      // case Emoji:
      //   setCurrentPopup({ ref: toolbarEmojiBtnRef, component: AdditionBtnStyle.Emoji });
      //   break;
      default:
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, style);
        break;
    }
  };
  const closePopup = () => {
    setCurrentPopup({ ref: null, component: null });
  };
  // toolbar button className handler
  const getActiveClsAndRefObj = (style: BtnStyleFormatType) => {
    switch (style) {
      case 'bold':
        return { isActive: isBold, refObj: undefined };
      case 'italic':
        return { isActive: isItalic, refObj: undefined };
      case 'strikethrough':
        return { isActive: isStrikethrough, refObj: undefined };
      case 'underline':
        return { isActive: isUnderline, refObj: undefined };
      case 'code':
        return { isActive: isCode, refObj: undefined };
      case 'highlight':
        return { isActive: isHighlight, refObj: undefined };
      case 'subscript':
        return { isActive: isSubscript, refObj: undefined };
      case 'superscript':
        return { isActive: isSuperscript, refObj: undefined };
      case Link:
        return { isActive: isLink, refObj: toolbarLinkBtnRef };
      case Table:
        return { isActive: false, refObj: toolbarTableBtnRef };
      // case Emoji:
      //   return { isActive: false, refObj: toolbarEmojiBtnRef };
      case Listol:
        return {
          isActive: listType === listTypeToListName.number,
          refObj: undefined,
        };
      case Listul:
        return {
          isActive: listType === listTypeToListName.bullet,
          refObj: undefined,
        };
      case Image:
        return { isActive: false, refObj: toolbarImageBtnRef };
      default:
        return { isActive: false, refObj: undefined };
    }
  };
  const getSubComponent = (style: BtnStyleFormatType) => {
    switch (style) {
      case AdditionBtnStyle.Image:
        return currentPopup.component === AdditionBtnStyle.Image && <></>;
      case AdditionBtnStyle.Table:
        return (
          currentPopup.component === AdditionBtnStyle.Table && <TablePopup activeEditor={editor} onClose={closePopup} />
        );
      case AdditionBtnStyle.Link:
        return (
          currentPopup.component === AdditionBtnStyle.Link && <LinkPopup activeEditor={editor} onClose={closePopup} />
        );
      // case AdditionBtnStyle.Emoji:
      //   return (
      //     currentPopup.component === AdditionBtnStyle.Emoji && <EmojiPopup activeEditor={editor} onClose={closePopup} />
      //   );
      default:
        return <></>;
    }
  };
  return (
    <span className={styles['editor-toolbar']} ref={toolbarRef}>
      {toolbarBtnList.map((btn) => {
        const { isActive, refObj } = getActiveClsAndRefObj(btn.style);
        const CClassname = cx('action-btn', { active: isActive });
        if (disabledFeatureList.includes(btn.style)) {
          return <></>;
        }
        return (
          <>
            <button
              {...(refObj ? { ref: refObj } : {})}
              key={btn.style}
              title={btn.style.toUpperCase()}
              className={CClassname}
              onClick={() => handleBtnClick(btn.style as BtnStyleFormatType)}
              data-testid={`toolbar-btn-${btn.style}`}
            >
              {btn.label}
              {getSubComponent(btn.style)}
            </button>
            {btn.divider && <Divider />}
          </>
        );
      })}
      {blockType in blockTypeToBlockName && activeEditor === editor && (
        <>
          <BlockFormatDropDown blockType={blockType} editor={editor} />
          <Divider />
        </>
      )}
    </span>
  );
};

export default Toolbar;
